import type { ComputedRef, Ref } from 'vue'
import type { PricingCategory } from './types/event'

/**
 * Category quantities map where keys are ticket category IDs (e.g., 'ADULT', 'CHILD')
 * and values are the quantity for each category
 */
export type CategoryQuantities = Record<string, number>

/**
 * Return type for useTicketQuantityCalculations composable
 */
export interface UseTicketQuantityCalculationsReturn {
  /**
   * Calculates the total number of tickets from a record of category quantities.
   * This is a pure function that doesn't react to changes.
   *
   * @example
   * ```ts
   * const { totalTickets } = useTicketQuantityCalculations()
   * const quantities = { ADULT: 2, CHILD: 1 }
   * const total = totalTickets(quantities) // Returns 3
   * ```
   */
  totalTickets: (categoryQuantities: CategoryQuantities) => number

  /**
   * Creates a computed ref that automatically calculates the total tickets
   * and reacts to changes in the categoryQuantities ref.
   *
   * @param categoryQuantities - Ref to a record mapping category IDs to quantities
   * @returns A computed ref that contains the total number of tickets
   *
   * @example
   * ```ts
   * const { totalTicketsComputed } = useTicketQuantityCalculations()
   * const categoryQuantities = ref({ ADULT: 2, CHILD: 1 })
   * const totalTickets = totalTicketsComputed(categoryQuantities)
   * // totalTickets.value is 3 and updates automatically when categoryQuantities changes
   * ```
   */
  totalTicketsComputed: (
    categoryQuantities: Ref<CategoryQuantities>
  ) => ComputedRef<number>

  /**
   * Initializes category quantities based on pricing categories.
   * If ADULT category exists, sets it to 1 and others to 0.
   * Otherwise, sets the first category to 1 and others to 0.
   *
   * @param categories - Array of pricing categories
   * @returns Initial category quantities map
   *
   * @example
   * ```ts
   * const { getInitialCategoryQuantities } = useTicketQuantityCalculations()
   * const categories = eventData.pricing.data.pricingCategories.data
   * const initialQuantities = getInitialCategoryQuantities(categories)
   * // Returns { ADULT: 1, CHILD: 0, SENIOR: 0 } if ADULT exists
   * // or { CHILD: 1, SENIOR: 0 } if ADULT doesn't exist
   * ```
   */
  getInitialCategoryQuantities: (
    categories: PricingCategory[]
  ) => CategoryQuantities
}

/**
 * Composable for calculating total tickets from category quantities.
 *
 * Provides utilities to sum up ticket quantities across different categories
 * (e.g., ADULT, CHILD, SENIOR).
 *
 * @returns Object containing functions to calculate total tickets
 *
 * @example
 * ```ts
 * const { totalTicketsComputed } = useTicketQuantityCalculations()
 * const categoryQuantities = ref({ ADULT: 2, CHILD: 1 })
 * const total = totalTicketsComputed(categoryQuantities)
 * // total.value === 3
 * ```
 */
export const useTicketQuantityCalculations =
  (): UseTicketQuantityCalculationsReturn => {
    /**
     * Calculates the total number of tickets from a record of category quantities
     */
    const totalTickets = (categoryQuantities: CategoryQuantities): number => {
      return Object.values(categoryQuantities).reduce(
        (sum, qty) => sum + qty,
        0
      )
    }

    /**
     * Creates a computed ref that reacts to changes in categoryQuantities
     */
    const totalTicketsComputed = (
      categoryQuantities: Ref<CategoryQuantities>
    ): ComputedRef<number> => {
      return computed(() => totalTickets(categoryQuantities.value))
    }

    /**
     * Initializes category quantities based on pricing categories.
     * Prioritizes ADULT category if available, otherwise uses the first category.
     */
    const getInitialCategoryQuantities = (
      categories: PricingCategory[]
    ): CategoryQuantities => {
      if (categories.length === 0) {
        return {}
      }

      const hasAdult = categories.some((cat) => cat.ticket_category === 'ADULT')
      const defaultCategory = hasAdult ? 'ADULT' : categories[0].ticket_category

      return categories.reduce((acc, cat) => {
        acc[cat.ticket_category] =
          cat.ticket_category === defaultCategory ? 1 : 0
        return acc
      }, {} as CategoryQuantities)
    }

    return {
      totalTickets,
      totalTicketsComputed,
      getInitialCategoryQuantities,
    }
  }
